import { useCallback } from 'react';
import {
  addParametersToSpecificFamilies,
  addProjectParameters,
  createParametersFromParsedFile,
} from '../../../api/revit/revitBridge';
import {
  type PendingRevitOperationRecoveryMetadata,
  type RevitOperationProgressSnapshot,
} from '../../../api/grpc/operationProgressClient';
import { buildParameterAdditionPayload } from '../../revit/parameterAdditionPayload';
import { mergeWithDefaultSharedParameters } from '../../revit/shippedSharedParameters';
import { applyParameterTypeHints } from '../../revit/wizardParameterDataTypes';
import { type ParameterBindingKind } from '../../revit/projectParameterMutationPlan';
import { invalidateFamilySnapshotCache } from '../../revit/familySnapshotCache';
import { buildUnavailableFamilyTargetResult } from '../../revit/parameterAdditionResultPolicy';
import {
  buildParsedFileCreationOutcome,
  buildProjectParameterAdditionOutcome,
  normalizeProjectParameterProgress,
  type ProjectBindingConflictMode,
} from '../projectParameterOperationOutcome';
import { useSharedParameterStore } from '../../../stores/useSharedParameterStore';
import { resolveSharedParameterDefinitions } from './configuration';
import { resolveProjectParameterBindingCategories } from './connectorPlan';
import { isNoEditableObjectTypeFamilyTargetError, summarizeHostFamilyParameterFeedback } from './feedback';
import { type FamilyParameterHostResponseData, type ParameterAdditionResult } from './models';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import {
  recordConnectorLiveEvidenceDispatch,
  recordConnectorLiveEvidenceDispatchOperationId,
  recordConnectorLiveEvidenceProgress,
  recordConnectorLiveEvidenceRawResult,
  recordConnectorLiveEvidenceRequest,
} from '../../../config/connectorLiveEvidence';

export function useParameterMutationActions(core: ParameterAdditionCore) {
  const {
    currentProjectUndoKeyRef,
    expectedDocumentIdentity,
    updateParameterProgressFromHost,
  } = core;

  /**
   * Adds parameters as PROJECT parameters using TypeBinding
   *
   * This is FAST because it doesn't open family documents.
   * Parameters are bound to categories at the project level and appear on all elements.
   */
  const addAsProjectParameters = useCallback(async (
    params: string[],
    options: {
      categories?: string[];
      bindingKind?: ParameterBindingKind;
      bindingConflictMode?: ProjectBindingConflictMode;
      parameterTypeHints?: Array<{ name: string; dataType: string }>;
    } = {},
  ): Promise<ParameterAdditionResult> => {
    const resolvedCategories = resolveProjectParameterBindingCategories(
      options.categories,
    );
    const parameterTypeHintMap = new Map(
      (options.parameterTypeHints ?? []).map((
        hint,
      ) => [hint.name.trim().toLowerCase(), hint.dataType]),
    );
    const recordProjectDispatch = (bindingKind: ParameterBindingKind) =>
      recordConnectorLiveEvidenceDispatch({
        destination: 'project',
        bindingKind,
        categories: [...resolvedCategories],
        expectedDocumentName: expectedDocumentIdentity?.expectedDocumentName ?? null,
        expectedDocumentPath: expectedDocumentIdentity?.expectedDocumentPath ?? null,
        operationId: null,
      });

    try {
      if ((options.bindingKind ?? 'type') === 'instance') {
        const { definitions, missingNames } = resolveSharedParameterDefinitions(
          params,
          // Known PipeEX parameters resolve to their shipped definitions even
          // when the loaded (custom) file does not define them.
          mergeWithDefaultSharedParameters(
            useSharedParameterStore.getState().parsedParameters,
          ),
        );

        // Names without a shared definition are still valid targets: Revit built-in
        // parameters need no binding, and any other name is created by the host as a
        // plain (non-shared) project parameter. They are sent with an empty GUID so
        // the host resolves them by name or creates them ad-hoc. When the caller
        // supplied a data-type hint (e.g. connector binding fields), use it so the
        // host can create custom-named parameters with the right storage type.
        const adHocDefinitions = missingNames.map((name) => ({
          name,
          guid: '',
          dataType: parameterTypeHintMap.get(name.trim().toLowerCase()) ?? '',
          groupId: 0,
          description: '',
        }));

        const backendParameters = [
            ...applyParameterTypeHints(
              definitions,
              options.parameterTypeHints ?? [],
            ),
            ...adHocDefinitions,
          ];
        const evidenceCallIndex = recordConnectorLiveEvidenceRequest({
          parameterNames: [...params],
          categories: [...resolvedCategories],
          requestedBindingKind: options.bindingKind ?? 'type',
          effectiveBindingKind: 'instance',
          parameterTypeHints: [...(options.parameterTypeHints ?? [])],
          backendParameters: backendParameters.map(({ name, dataType }) => ({ name, dataType })),
        });
        const evidenceDispatchIndex = recordProjectDispatch('instance');
        const result = await createParametersFromParsedFile({
          parameters: backendParameters,
          categories: resolvedCategories,
          isType: false,
          ...expectedDocumentIdentity,
          onProgress: (progress) => {
            const normalizedProgress = normalizeProjectParameterProgress(
              progress,
              options.bindingConflictMode,
            );
            if (normalizedProgress.operationId) {
              recordConnectorLiveEvidenceDispatchOperationId(
                evidenceDispatchIndex,
                normalizedProgress.operationId,
              );
            }
            recordConnectorLiveEvidenceProgress(normalizedProgress);
            updateParameterProgressFromHost(normalizedProgress);
          },
        });
        recordConnectorLiveEvidenceRawResult(evidenceCallIndex, result);

        // Aggregate strictly from per-item results: the host aggregate flag
        // alone cannot distinguish tolerated binding conflicts from genuine
        // failures, and a missing terminal outcome must block navigation.
        return buildParsedFileCreationOutcome({
          result,
          requestedCount: params.length,
          requestedParameters: params,
          bindingConflictMode: options.bindingConflictMode,
        });
      }

      const evidenceCallIndex = recordConnectorLiveEvidenceRequest({
        parameterNames: [...params],
        categories: [...resolvedCategories],
        requestedBindingKind: options.bindingKind ?? 'type',
        effectiveBindingKind: options.bindingKind ?? 'type',
        parameterTypeHints: [...(options.parameterTypeHints ?? [])],
        backendParameters: params.map((name) => ({
          name,
          dataType: parameterTypeHintMap.get(name.trim().toLowerCase()) ?? '',
        })),
      });
      const evidenceDispatchIndex = recordProjectDispatch(options.bindingKind ?? 'type');
      const result = await addProjectParameters(
        params,
        resolvedCategories,
        expectedDocumentIdentity,
        {
          onProgress: (progress) => {
            const normalizedProgress = normalizeProjectParameterProgress(
              progress,
              options.bindingConflictMode,
            );
            if (normalizedProgress.operationId) {
              recordConnectorLiveEvidenceDispatchOperationId(
                evidenceDispatchIndex,
                normalizedProgress.operationId,
              );
            }
            recordConnectorLiveEvidenceProgress(normalizedProgress);
            updateParameterProgressFromHost(normalizedProgress);
          },
          parameterTypeHints: options.parameterTypeHints,
          bindingKind: options.bindingKind ?? 'type',
        },
      );
      recordConnectorLiveEvidenceRawResult(evidenceCallIndex, result);

      const outcome = buildProjectParameterAdditionOutcome({
        result,
        requestedCount: params.length,
        requestedParameters: params,
        bindingConflictMode: options.bindingConflictMode,
      });
      if (!outcome.success) {
        console.error(
          '[useParameterAddition] Failed to add project parameters:',
          outcome.error,
        );
      }
      return outcome;
    } catch (error) {
      console.error('[useParameterAddition] Exception:', error);
      return {
        success: false,
        addedCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [expectedDocumentIdentity, updateParameterProgressFromHost]);

  const addToConfiguredFamilyCategories = useCallback(async (
    params: string[],
    categories: string[],
    bindingKind: ParameterBindingKind,
    options?: {
      targetFamilyIds?: string[];
      expectedFamilyCount?: number;
      onProgress?: (progress: RevitOperationProgressSnapshot) => void;
      parameterTypeHints?: Array<{ name: string; dataType: string }>;
      pendingOperationRecovery?: PendingRevitOperationRecoveryMetadata;
      requireReversibleMutations?: boolean;
    },
  ): Promise<ParameterAdditionResult> => {
    const explicitFamilyIds = Array.from(
      new Set(options?.targetFamilyIds ?? []),
    );
    // Keep the category filter alongside explicit ids: the backend falls back
    // to it when a snapshot id no longer resolves in the current document.
    const payload = buildParameterAdditionPayload(params, {
      categories,
      bindingKind,
      parameterTypeHints: options?.parameterTypeHints,
    });
    const result = await addParametersToSpecificFamilies(
      explicitFamilyIds,
      params,
      false,
      {
        categories: payload.categories,
        sharedParameterFilePath: payload.sharedParameterFilePath,
        sharedParameterFileContent: payload.sharedParameterFileContent,
        bindingKind: payload.bindingKind,
        parameterTypeHints: payload.parameterTypeHints,
        ...expectedDocumentIdentity,
        pendingOperationRecovery: options?.pendingOperationRecovery,
        requireReversibleMutations: options?.requireReversibleMutations,
        onProgress: updateParameterProgressFromHost,
      },
    );
    invalidateFamilySnapshotCache(currentProjectUndoKeyRef.current);
    const responseData = result.data as
      | FamilyParameterHostResponseData
      | undefined;

    if (!result.success) {
      return {
        success: false,
        addedCount: 0,
        modifiedFamilyCount: responseData?.modifiedFamiliesCount,
        familyUndoEntries: responseData?.undoEntries,
        projectUndoEntries: responseData?.projectUndoEntries,
        error: result.error || 'Failed to queue family parameter creation',
      };
    }

    const hostFeedback = summarizeHostFamilyParameterFeedback(
      responseData?.message,
      responseData?.error || result.error,
    );
    const failedCount = responseData?.failureCount ?? hostFeedback.failedCount;
    const conflictingCount = responseData?.conflictCount ??
      hostFeedback.conflictingCount;
    return {
      success: failedCount === 0 && conflictingCount === 0,
      addedCount: 0,
      existingCount: responseData?.existingFamilyParameterCount,
      familyCount: Number(
        responseData?.processedFamilyTargetCount ??
          responseData?.resolvedFamilyTargetCount ??
          responseData?.modifiedFamiliesCount ??
          options?.expectedFamilyCount ??
          0,
      ),
      modifiedFamilyCount: responseData?.modifiedFamiliesCount,
      resolvedFamilyTargetCount: responseData?.resolvedFamilyTargetCount,
      processedFamilyTargetCount: responseData?.processedFamilyTargetCount,
      openedFamilyCount: responseData?.openedFamilyCount,
      unmodifiedFamilyTargetCount: responseData?.unmodifiedFamilyTargetCount,
      hasLoadableFamilies: true,
      hasSystemFamilies: false,
      queued: responseData?.status === 'queued',
      requestedCount: params.length,
      failedCount,
      conflictingCount,
      familyUndoEntries: responseData?.undoEntries,
      projectUndoEntries: responseData?.projectUndoEntries,
      error: failedCount > 0 || conflictingCount > 0 ? hostFeedback.detail : undefined,
    };
  }, [
    currentProjectUndoKeyRef,
    expectedDocumentIdentity,
    updateParameterProgressFromHost,
  ]);

  const addToObjectTypeFamilies = useCallback(async (
    params: string[],
    targetObjectTypes: string[],
    bindingKind: ParameterBindingKind = 'type',
    options?: {
      targetFamilyIds?: string[];
      expectedFamilyCount?: number;
      objectTypeParameterName: string;
      onProgress?: (progress: RevitOperationProgressSnapshot) => void;
      parameterTypeHints?: Array<{ name: string; dataType: string }>;
    },
  ): Promise<ParameterAdditionResult> => {
    try {
      const explicitFamilyIds = Array.from(
        new Set(options?.targetFamilyIds ?? []),
      );
      const payload = buildParameterAdditionPayload(params, {
        // Same fallback contract: explicit ids first, object types when ids go stale.
        targetObjectTypes,
        objectTypeParameterName: options?.objectTypeParameterName,
        bindingKind,
        parameterTypeHints: options?.parameterTypeHints,
      });
      const result = await addParametersToSpecificFamilies(
        explicitFamilyIds,
        params,
        false,
        {
          categories: payload.categories,
          sharedParameterFilePath: payload.sharedParameterFilePath,
          sharedParameterFileContent: payload.sharedParameterFileContent,
          bindingKind: payload.bindingKind,
          targetObjectTypes: payload.targetObjectTypes,
          objectTypeParameterName: payload.objectTypeParameterName,
          parameterTypeHints: payload.parameterTypeHints,
          ...expectedDocumentIdentity,
          onProgress: options?.onProgress ?? updateParameterProgressFromHost,
        },
      );
      invalidateFamilySnapshotCache(currentProjectUndoKeyRef.current);
      const responseData = result.data as
        | FamilyParameterHostResponseData
        | undefined;

      if (!result.success) {
        if (isNoEditableObjectTypeFamilyTargetError(result.error)) {
          return buildUnavailableFamilyTargetResult({
            parameterNames: params,
            targetLabels: targetObjectTypes,
          });
        }

        return {
          success: false,
          addedCount: 0,
          modifiedFamilyCount: responseData?.modifiedFamiliesCount,
          familyUndoEntries: responseData?.undoEntries,
          projectUndoEntries: responseData?.projectUndoEntries,
          error: result.error ||
            'Failed to add parameters to matching component families',
        };
      }

      const hostFeedback = summarizeHostFamilyParameterFeedback(
        responseData?.message,
        responseData?.error || result.error,
      );
      const failedCount = responseData?.failureCount ??
        hostFeedback.failedCount;
      const conflictingCount = responseData?.conflictCount ??
        hostFeedback.conflictingCount;
      return {
        success: failedCount === 0 && conflictingCount === 0,
        addedCount: 0,
        existingCount: responseData?.existingFamilyParameterCount,
        familyCount: responseData?.processedFamilyTargetCount ??
          responseData?.resolvedFamilyTargetCount ??
          options?.expectedFamilyCount ??
          (explicitFamilyIds.length || targetObjectTypes.length),
        modifiedFamilyCount: responseData?.modifiedFamiliesCount,
        resolvedFamilyTargetCount: responseData?.resolvedFamilyTargetCount,
        processedFamilyTargetCount: responseData?.processedFamilyTargetCount,
        openedFamilyCount: responseData?.openedFamilyCount,
        unmodifiedFamilyTargetCount: responseData?.unmodifiedFamilyTargetCount,
        hasLoadableFamilies: true,
        hasSystemFamilies: false,
        queued: responseData?.status === 'queued',
        requestedCount: params.length,
        failedCount,
        conflictingCount,
        familyUndoEntries: responseData?.undoEntries,
        projectUndoEntries: responseData?.projectUndoEntries,
        error: failedCount > 0 || conflictingCount > 0 ? hostFeedback.detail : undefined,
      };
    } catch (error) {
      return {
        success: false,
        addedCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [
    currentProjectUndoKeyRef,
    expectedDocumentIdentity,
    updateParameterProgressFromHost,
  ]);

  return {
    addAsProjectParameters,
    addToConfiguredFamilyCategories,
    addToObjectTypeFamilies,
  };
}

export type ParameterMutationActions = ReturnType<
  typeof useParameterMutationActions
>;
